import { useCallback, useEffect, useState } from "react";
import "./AddStaff.css";
import { Link, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Row, Col, Form, InputGroup, Button } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import { ToastContainer, toast } from "react-toastify";
import Switch from "@mui/material/Switch";
import FormControl from "@mui/material/FormControl";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useDispatch, useSelector } from "react-redux";
import { createEmployee, initEmpState } from "../../../redux/slices/employeeSlice";
import { fetchBranchData, initBranchState } from "../../../redux/slices/branchSlice";
import { fetchDepartmentData, initDepartState } from "../../../redux/slices/departmentSlice";
import { commonGetData, initCommonState } from "../../../redux/slices/commonSlice";
import { fetchDesignationList, initDesigState } from "../../../redux/slices/designationSlice";
import { getEmployerIdListPath } from "../../../services/config";
import { HasPermission } from "../../../services/hasPermission";

const animatedComponents = makeAnimated();

// ─── Theme-aware react-select styles ─────────────────────────────────────────
const getCustomStyles = (isDark) => ({
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (provided, state) => ({
    ...provided,
    border: state.isFocused
      ? `1px solid ${isDark ? "#8660F8" : "#6C63FF"}`
      : `1px solid ${isDark ? "#3D3A58" : "#E0E0F0"}`,
    borderRadius: "8px",
    boxShadow: state.isFocused
      ? `0 0 0 3px ${isDark ? "rgba(134,96,248,0.18)" : "rgba(108,99,255,0.15)"}`
      : "none",
    backgroundColor: isDark ? "#161421" : "#FFFFFF",
    minHeight: "38px",
    "&:hover": { borderColor: isDark ? "#8660F8" : "#6C63FF" },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused
      ? isDark ? "rgba(134,96,248,0.15)" : "rgba(108,99,255,0.08)"
      : state.isSelected
        ? isDark ? "#8660F8" : "#6C63FF"
        : isDark ? "#19152C" : "#FFFFFF",
    color: state.isSelected ? "#FFFFFF" : isDark ? "#E3E2EB" : "#1A1A2E",
    cursor: "pointer",
    fontSize: "12px",
    fontFamily: "'Outfit', sans-serif",
    "&:active": {
      backgroundColor: isDark ? "#8660F8" : "#6C63FF",
    },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: isDark ? "#19152C" : "#FFFFFF",
    border: `1px solid ${isDark ? "#2D2A45" : "#E0E0F0"}`,
    borderRadius: "8px",
    boxShadow: isDark
      ? "0 8px 32px rgba(0,0,0,0.4)"
      : "0 8px 24px rgba(0,0,0,0.08)",
    zIndex: 9999,
  }),
  menuList: (provided) => ({ ...provided, padding: "4px" }),
  singleValue: (provided) => ({
    ...provided,
    color: isDark ? "#E3E2EB" : "#1A1A2E",
    fontSize: "12px",
    fontFamily: "'Outfit', sans-serif",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: isDark
      ? "rgba(134,96,248,0.15)"
      : "rgba(108,99,255,0.1)",
    borderRadius: "4px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: isDark ? "#8660F8" : "#6C63FF",
    fontSize: "11px",
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 600,
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: isDark ? "#8660F8" : "#6C63FF",
    borderRadius: "0 4px 4px 0",
    "&:hover": {
      backgroundColor: "rgba(239,68,68,0.15)",
      color: "#EF4444",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: isDark ? "#9998A2" : "#8A8AA8",
    fontSize: "12px",
    fontFamily: "'Outfit', sans-serif",
  }),
  input: (provided) => ({
    ...provided,
    color: isDark ? "#E3E2EB" : "#1A1A2E",
    fontSize: "12px",
    fontFamily: "'Outfit', sans-serif",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: isDark ? "#9998A2" : "#8A8AA8",
    padding: "0 8px",
    "&:hover": { color: isDark ? "#8660F8" : "#6C63FF" },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: isDark ? "#9998A2" : "#8A8AA8",
    "&:hover": { color: "#EF4444" },
  }),
});

const AddStaff = () => {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const employeeState    = useSelector((state) => state.employee);
  const designationStore = useSelector((state) => state.designation);
  const branchState      = useSelector((state) => state.branch);
  const departmentState  = useSelector((state) => state.department);
  const commonState      = useSelector((state) => state.common);
  const themeStore       = useSelector((state) => state.theme);

  // ── Theme detection ─────────────────────────────────────────────────────────
  const isDark       = themeStore?.theme === "dark" || !themeStore?.theme;
  const customStyles = getCustomStyles(isDark);

  // ── Derived theme tokens ─────────────────────────────────────────────────────
  const th = {
    bgPage:      isDark ? "#0D0B19"  : "#F5F7FA",
    bgCard:      isDark ? "#19152C"  : "#FFFFFF",
    bgInput:     isDark ? "#161421"  : "#FFFFFF",
    textPrimary: isDark ? "#E3E2EB"  : "#1A1A2E",
    textMuted:   isDark ? "#9998A2"  : "#6B6B8A",
    border:      isDark ? "#2D2A45"  : "#E0E0F0",
    borderInput: isDark ? "#3D3A58"  : "#E0E0F0",
    primary:     isDark ? "#8660F8"  : "#6C63FF",
    gradient:    isDark
      ? "linear-gradient(135deg,#8660F8,#7550E8)"
      : "linear-gradient(135deg,#6C63FF,#5A52D5)",
    danger:      "#EF4444",
  };

  const [checked,    setChecked]    = useState(false);
  const [branchList, setBranchList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [employerList,   setEmployerList]   = useState([]);
  const [designationList, setDesignationList] = useState([]);
  const [show,         setShow]         = useState(false);
  const [moduleError,  setModuleError]  = useState(false);
  const [validated,    setValidated]    = useState(false);
  const [saveBranch,   setSaveBranch]   = useState({ modules: [] });

  const user = localStorage.getItem("user_data")
    ? JSON.parse(localStorage.getItem("user_data"))
    : null;

  const [addStaff, setAddStaff] = useState({
    userId: user?.user_id,
    name: "", jobTitle: "", branch: "", department: "",
    code: "+91", number: "", loginOtp: "", empId: "", gender: "",
    personalEmailId: "", officeEmailId: "", dateOfJoining: "",
    address: "", isEnableManualEmpId: "", empCustomNumber: "",
    designationId: "",
  });

  // ── Fetch initial data ───────────────────────────────────────────────────────
  const geDataLists = useCallback(() => {
    dispatch(fetchBranchData(user.user_id));
    dispatch(fetchDepartmentData(user.user_id));
    dispatch(commonGetData(`${getEmployerIdListPath}${user.user_id}`));
    dispatch(fetchDesignationList(user.user_id));
  }, []);

  useEffect(() => { geDataLists(); }, []);

  // ── Process store responses ──────────────────────────────────────────────────
  useEffect(() => {
    if (departmentState.depGetSuccess) {
      setDepartmentList(
        departmentState.depGetData.map((v) => ({
          label: v.department_name, value: v.departmentId,
        }))
      );
      dispatch(initCommonState());
    }
    if (branchState.brnGetSuccess) {
      setBranchList(
        branchState.brnGetData.map((v) => ({
          label: v.branch_name, value: v.branchId,
        }))
      );
      dispatch(initBranchState());
    }
    if (commonState.commonGetSuccess) {
      if (commonState.commonGetData.length < 1) { handleShow(); return; }
      setEmployerList(commonState.commonGetData);
      dispatch(initDepartState());
    }
    if (designationStore.desigSuccess) {
      setDesignationList(
        designationStore.desigData?.map((v) => ({
          label: v.designation_name, value: v.designationId,
        }))
      );
      dispatch(initDesigState());
    }
  }, [departmentState, branchState, commonState, designationStore]);

  // ── Employee save result ─────────────────────────────────────────────────────
  useEffect(() => {
    if (employeeState.empSuccess) {
      toast.success(employeeState.empMessage);
      dispatch(initEmpState());
      setAddStaff({
        name: "", jobTitle: "", branch: "", department: "", code: "+91",
        number: "", loginOtp: "", empId: "", gender: "", personalEmailId: "",
        officeEmailId: "", dateOfJoining: "", address: "", empCustomNumber: "",
      });
      setChecked(false);
      setSaveBranch({ modules: [] });
      navigate("/empmanagement");
    }
    if (employeeState.empError) {
      toast.error(employeeState.empMessage);
      dispatch(initEmpState());
    }
  }, [employeeState.empSuccess, employeeState.empError]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleModalClose  = () => setShow(false);
  const handleShow        = () => setShow(true);
  const handleDeleteSubmit = () => { navigate("/setting/my-employee-id"); handleModalClose(); };

  const handleBranchChange = (e) => {
    setSaveBranch({ ...saveBranch, modules: e });
    setModuleError(false);
  };

  const handleChange = (event) => {
    const on = event.target.checked;
    setChecked(on);
    setAddStaff((pv) => ({
      ...pv,
      isEnableManualEmpId: on ? 1 : 0,
      ...(on ? {} : { empCustomNumber: "" }),
    }));
  };

  const handleCustomChange = (e) => {
    const { value } = e.target;
    if (/^\d*$/.test(value)) setAddStaff((pv) => ({ ...pv, empCustomNumber: value }));
  };

  const handelChange = (e) => {
    const { name, value } = e.target;
    setAddStaff((pv) => ({ ...pv, [name]: value }));
  };

  const checkLengthHandle = (e) => {
    const { name, value } = e.target;
    if (value === "" || (/^[0-9\b]+$/.test(value) && value.length <= 8))
      setAddStaff((pv) => ({ ...pv, [name]: value }));
  };

  const handleKeyPress = (e) => {
    if (e.key !== "Backspace" && !/[0-9]/.test(e.key)) e.preventDefault();
  };

  const handelSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      setValidated(true);
      if (saveBranch.modules.length < 1) setModuleError(true);
      return;
    }
    dispatch(createEmployee({
      userId:              user?.user_id,
      cmp_name:            user?.company_name,
      fullname:            addStaff.name,
      designationId:       addStaff.designationId,
      branch:              saveBranch.modules.map((v) => v.value).toString(),
      department:          addStaff.department,
      emp_mobile:          addStaff.number,
      pwd:                 addStaff.loginOtp,
      empId:               addStaff.empId,
      gender:              addStaff.gender,
      personalEmailId:     addStaff.personalEmailId,
      office_email:        addStaff.personalEmailId,
      dateof_join:         addStaff.dateOfJoining,
      address:             addStaff.address,
      isEnableManualEmpId: addStaff.isEnableManualEmpId,
      empCustomNumber:     addStaff.empCustomNumber,
    }));
  };

  // ── Field definitions ────────────────────────────────────────────────────────
  const inputsOne = [
    { type: "text",  name: "name",         label: "Name",        placeholder: "Enter Name",         required: true },
    { type: "text",  name: "designationId",label: "Job Title",   placeholder: "Select Job Title",   isMulti: false, chi: designationList },
    { type: "text",  name: "branch",        label: "Branch",      placeholder: "Select Branch",      required: true, isMulti: true,  chi: branchList },
    { type: "text",  name: "department",    label: "Department",  placeholder: "Select Department",  isMulti: false, chi: departmentList },
  ];

  const inputsTwo = [
    { type: "email", name: "personalEmailId", required: true, label: "Personal Email Id", placeholder: "Enter Personal Email Id" },
    { type: "text",  name: "empId",            required: true, label: "Employee Id",       chi: employerList },
  ];

  // ── Inline style helpers ─────────────────────────────────────────────────────
  const labelStyle = {
    fontFamily: "'Outfit', sans-serif",
    fontSize:   "12px",
    fontWeight: 500,
    color:      th.textMuted,
    marginBottom: "5px",
    display: "block",
  };

  const hintStyle = {
    fontFamily: "'Outfit', sans-serif",
    fontSize:   "12px",
    color:      th.textMuted,
    marginTop:  "5px",
    display:    "block",
  };

  const inputGroupTextStyle = {
    height:                "38px",
    backgroundColor:       th.bgInput,
    color:                 th.textMuted,
    border:                `1px solid ${th.borderInput}`,
    borderRight:           "none",
    borderTopLeftRadius:   "8px",
    borderBottomLeftRadius:"8px",
    fontFamily:            "'Outfit', sans-serif",
    fontSize:              "12px",
  };

  const mobileInputStyle = {
    height:                 "38px",
    backgroundColor:        th.bgInput,
    color:                  th.textPrimary,
    border:                 `1px solid ${th.borderInput}`,
    borderLeft:             "none",
    borderTopRightRadius:   "8px",
    borderBottomRightRadius:"8px",
    fontFamily:             "'Outfit', sans-serif",
    fontSize:               "12px",
    zIndex:                 0,
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Modal ── */}
      <Modal show={show} backdrop="static">
        <Modal.Header
          closeButton={false}
          style={{
            backgroundColor: th.bgCard,
            borderBottom:    `1px solid ${th.border}`,
          }}
        >
          <Modal.Title
            style={{
              color:      th.textPrimary,
              fontFamily: "'Outfit', sans-serif",
              fontSize:   "14px",
              fontWeight: 600,
            }}
          >
            Please Create an Employer id first to add staff here.
          </Modal.Title>
        </Modal.Header>
        <Modal.Footer
          style={{
            backgroundColor: th.bgCard,
            borderTop:       `1px solid ${th.border}`,
          }}
        >
          <Button
            variant="info"
            style={{ color: "#fff" }}
            onClick={handleDeleteSubmit}
          >
            GO
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" autoClose={2000} className="toast-custom" />

      {/* ── Back bar ── */}
      <div
        style={{
          backgroundColor: th.bgCard,
          borderBottom:    `1px solid ${th.border}`,
          padding:         "12px 20px",
          display:         "flex",
          alignItems:      "center",
          gap:             "10px",
        }}
      >
        <Link
          to="/empmanagement"
          style={{ color: th.textPrimary, display: "flex", alignItems: "center" }}
        >
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </Link>
        <p
          style={{
            margin:     0,
            fontFamily: "'Outfit', sans-serif",
            fontSize:   "15px",
            fontWeight: 600,
            color:      th.textPrimary,
          }}
        >
          Add Staff
        </p>
      </div>

      {/* ── Main content ── */}
      <div style={{ backgroundColor: th.bgPage, minHeight: "calc(100vh - 57px)", padding: "20px" }}>
        <div
          style={{
            backgroundColor: th.bgCard,
            border:          `1px solid ${th.border}`,
            borderRadius:    "12px",
            padding:         "24px",
          }}
        >
          <Form noValidate validated={validated} onSubmit={handelSubmit}>
            <Row>
              {/* ── inputsOne ── */}
              {inputsOne.map((item, i) => (
                <Col sm="6" className="mt-3" key={i}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>
                      {item.label}
                      {item.required && (
                        <span style={{ color: th.danger, marginLeft: "2px" }}>*</span>
                      )}
                    </Form.Label>

                    {/* Multi-select (Branch) */}
                    {item.isMulti && item.chi ? (
                      <>
                        <div
                          style={{
                            border:       moduleError ? `1px solid ${th.danger}` : "none",
                            borderRadius: "8px",
                          }}
                        >
                          <Select
                            closeMenuOnSelect={false}
                            components={animatedComponents}
                            value={saveBranch.modules}
                            name={item.name}
                            isMulti
                            placeholder="Select branch"
                            options={item.chi}
                            onChange={handleBranchChange}
                            styles={customStyles}
                            menuPortalTarget={document.body}
                          />
                        </div>
                        {moduleError && (
                          <span
                            style={{
                              color:      th.danger,
                              fontSize:   "11px",
                              fontFamily: "'Outfit', sans-serif",
                              marginTop:  "4px",
                              display:    "block",
                            }}
                          >
                            Please select a valid branch
                          </span>
                        )}
                      </>

                    /* Single-select dropdown */
                    ) : !item.isMulti && item.chi ? (
                      <>
                        <Form.Select
                          name={item.name}
                          onChange={handelChange}
                          value={addStaff[item.name]}
                          className="universal-input"
                        >
                          <option value="">{item.placeholder}</option>
                          {item.chi.map((v, id) => (
                            <option value={v.value} key={id}>{v.label}</option>
                          ))}
                          {item.chi.length < 1 && (
                            <option value="">Add Department</option>
                          )}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          Please provide a valid {item.label}.
                        </Form.Control.Feedback>
                      </>

                    /* Plain text input */
                    ) : (
                      <>
                        <Form.Control
                          name={item.name}
                          type={item.type || "text"}
                          placeholder={item.placeholder}
                          value={addStaff[item.name]}
                          onChange={handelChange}
                          required={!!item.required}
                          className="universal-input"
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide a valid {item.label}.
                        </Form.Control.Feedback>
                      </>
                    )}
                  </Form.Group>
                </Col>
              ))}

              {/* ── Mobile number ── */}
              <Col sm="6" className="mt-3">
                <Form.Group>
                  <Form.Label style={labelStyle}>
                    Mobile Number <span style={{ color: th.danger }}>*</span>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text id="basic-addon1" style={inputGroupTextStyle}>
                      +91
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="number"
                      pattern="\d*"
                      maxLength="10"
                      placeholder="Enter Mobile Number"
                      aria-label="number"
                      aria-describedby="basic-addon1"
                      required
                      value={addStaff.number}
                      onChange={handelChange}
                      onKeyDown={handleKeyPress}
                      style={mobileInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid mobile number.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>

              {/* ── inputsTwo ── */}
              {inputsTwo.map((item, i) =>
                item.chi ? (
                  <Col sm="6" className="mt-3" key={i}>
                    <Form.Group>
                      <Form.Label style={labelStyle}>
                        {item.label}
                        {item.required && (
                          <span style={{ color: th.danger, marginLeft: "2px" }}>*</span>
                        )}
                      </Form.Label>
                      <Form.Select
                        required
                        name={item.name}
                        onChange={handelChange}
                        value={addStaff[item.name]}
                        className="universal-input"
                      >
                        <option value="">Select Employee ID Prefix</option>
                        {item.chi.map((dv, idx) => (
                          <option key={idx} value={dv.employerId}>{dv.employerIdName}</option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        Please select a valid option.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                ) : (
                  <Col sm={item.type === "date" ? "12" : "6"} key={i} className="mt-3">
                    <Form.Group>
                      <Form.Label style={labelStyle}>
                        {item.label}
                        {item.required && (
                          <span style={{ color: th.danger, marginLeft: "2px" }}>*</span>
                        )}
                      </Form.Label>
                      <Form.Control
                        name={item.name}
                        type={item.type || "text"}
                        value={addStaff[item.name]}
                        placeholder={item.placeholder}
                        onChange={handelChange}
                        required={!!item.required}
                        className="universal-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a valid {item.label}.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                )
              )}

              {/* ── Manual assignment switch ── */}
              <Col sm="6" className="mt-4">
                <Form.Group>
                  <Form.Label style={labelStyle}>Manual Assignment Enabled</Form.Label>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <Switch
                      name="isEnableManualEmpId"
                      checked={checked}
                      onChange={handleChange}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                    {!checked && (
                      <span style={hintStyle}>
                        Allocate ID number Manually (Disabling this the system
                        will auto allocate the number in sequence)
                      </span>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* ── Custom number (shown when switch is ON) ── */}
            {checked && (
              <Row className="mt-4">
                <Col sm="12">
                  <Form.Group>
                    <Form.Label style={labelStyle}>Employee Custom Number</Form.Label>
                    <Form.Control
                      name="empCustomNumber"
                      value={addStaff.empCustomNumber}
                      onChange={handleCustomChange}
                      placeholder="Employee Custom Number"
                      className="universal-input"
                    />
                    <span style={hintStyle}>
                      Enter the required number in this format (sequence selected
                      in the backend when adding the Employee ID Prefix basis on
                      the selection of employee ID prefix)
                    </span>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {/* ── Submit ── */}
            {HasPermission("Employee", "create") && (
              <div
                style={{
                  marginTop:      "28px",
                  display:        "flex",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="submit"
                  disabled={!!employeeState.empLoading}
                  className="universal-btn"
                >
                  {employeeState.empLoading ? "Saving..." : "Save"}
                </button>
              </div>
            )}
          </Form>
        </div>
      </div>
    </>
  );
};

export default AddStaff;